/**
 * Update Profile API
 *
 * API functions for updating contractor profiles
 */

import { buildApiUrl, handleApiResponse } from '@/src/shared/api';
import { fetchWithTokenRefresh } from '@/src/shared/api/refreshToken';
import type { ContractorProfile } from '@hummii/shared-types';

export interface UpdateContractorProfileDto {
  bio?: string;
  experience?: number;
  hourlyRate?: number;
  businessName?: string;
  isAvailable?: boolean;
}

export interface UpdateLocationDto {
  latitude: number; // Required by backend
  longitude: number; // Required by backend
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  serviceRadius?: number;
}

/**
 * Update contractor profile
 */
export async function updateContractorProfile(
  dto: UpdateContractorProfileDto
): Promise<ContractorProfile> {
  const url = buildApiUrl('/contractors/me');

  const response = await fetchWithTokenRefresh(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for auth
    body: JSON.stringify(dto),
  });

  return handleApiResponse<ContractorProfile>(response);
}

/**
 * Update contractor location
 */
export async function updateContractorLocation(
  dto: UpdateLocationDto
): Promise<ContractorProfile> {
  const url = buildApiUrl('/contractors/me/location');

  const response = await fetchWithTokenRefresh(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(dto),
  });

  return handleApiResponse<ContractorProfile>(response);
}

/**
 * Get current contractor profile
 */
export async function getMyContractorProfile(): Promise<ContractorProfile> {
  const url = buildApiUrl('/contractors/me');

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    credentials: 'include',
  });

  return handleApiResponse<ContractorProfile>(response);
}
